import React, { useCallback, useEffect, useState } from 'react';

import classNames from 'clsx';

import { FormGroup, TextField } from '@mui/material';

import BedbankService from '~/services/BedbankService';
import SearchService, { TypeaheadResult, TypeaheadType } from '~/services/SearchService';

const RELEVANT_TYPEAHEAD_TYPES: Array<TypeaheadType> = ['le_property', 'le_property_unique_stays'];
export interface Result {
  id?: string;
  name: string;
  type: string;
  secondary: string;
  fk?: string;
}

interface Props {
  onSelect: (selection: Result, id: string) => void;
  label: string;
  id: string;
  type: string;
}

export function HotelSearchForm({ onSelect, id, type, label }: Props) {
  const [results, setResults] = useState<Array<Result>>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');

  const search = async (query: string) => {
    setValue(query);
    setOpen(true);
    setResults([]);
  };

  const bedbankSearch = useCallback(async () => {
    if (value.length < 2) {
      setOpen(false);
      setResults([]);
      return;
    }

    if (type === 'le') {
      const res = await SearchService.typeahead(value, {
        type: RELEVANT_TYPEAHEAD_TYPES,
        brand: 'luxuryescapes',
        region: 'AU',
        limit: 20,
        priority: 'current',
        vertical: 'hotel',
      });

      setResults(
        res.result.map((result: TypeaheadResult) => ({
          id: result.id,
          name: result.format.mainText,
          type: result.searchType,
          secondary: result.format.secondaryText,
          fk: result.offerId,
        })),
      );
      return;
    }

    const baseParams = {
      status: 'content-approved',
    };
    const nameParams = {
      ...baseParams,
      query: value,
      type: BedbankService.isBedbankId(value) ? 'id' : 'name',
    };
    const countryParams = {
      ...baseParams,
      country: value,
    };
    const stateParams = {
      ...baseParams,
      stateProvince: value,
    };
    const cityParams = {
      ...baseParams,
      city: value,
    };

    const [nameRes, countryRes, stateRes, cityRes] = await Promise.all([
      BedbankService.getProperties(1, 20, nameParams),
      BedbankService.getProperties(1, 20, countryParams),
      BedbankService.getProperties(1, 20, stateParams),
      BedbankService.getProperties(1, 20, cityParams),
    ]);

    const allResults = [...nameRes.result, ...countryRes.result, ...stateRes.result, ...cityRes.result];
    const uniqueResults = allResults.reduce(
      (unique, item) =>
        unique.find((uniqueItem) => item.id === uniqueItem.id && item.domain !== 'led') ? unique : [...unique, item],
      [],
    );
    setResults(
      uniqueResults.map((result) => ({
        id: result.id,
        name: result.name,
        type: result.type,
        secondary: result.id,
      })),
    );
  }, [value]);

  useEffect(() => {
    const b = setTimeout(bedbankSearch, 500);

    return () => clearTimeout(b);
  }, [bedbankSearch]);

  const select = (e, result: Result) => {
    e.preventDefault();
    onSelect(result, id);
    setOpen(false);
  };

  return (
    <FormGroup>
      <TextField
        id="PlaceSearch"
        data-testid="PlaceSearch"
        type="text"
        name="searchString"
        label={label}
        placeholder={label}
        value={value}
        onChange={(e) => search((e.target as HTMLInputElement).value)}
        autoFocus
        fullWidth
        sx={{ bgcolor: 'white' }}
      />

      <div className="place-search__filters">
        <div
          className={classNames('place-search__item dropdown', {
            open,
          })}
        >
          <ul className="dropdown-menu">
            {results.map((result) => (
              <li key={result.id ?? result.fk}>
                <a href="#" onClick={(e) => select(e, result)}>
                  <span>
                    {result.name} <small>{`${result.type} | ${result.secondary}`}</small>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </FormGroup>
  );
}
